/**
 * Usage Limits Store
 *
 * Handles usage limits and upgrade flows for any plan tier.
 * Catches 402 MEMBER_BUDGET_EXCEEDED responses, surfaces the appropriate
 * upgrade modal variant, and optionally resumes the blocked request after
 * the user upgrades (via pendingRequest + handleUpgradeResume).
 */

import { create } from 'zustand';
import { createFileLogger } from '../utils/logger';
import { useThinkingLoaderStore } from '../components/Space/SpaceChat/store/thinkingLoaderStore';

const log = createFileLogger('USAGE_LIMITS_STORE');

export type UsageLimitVariant =
  | 'upgrade'           // normal "upgrade your plan" flow
  | 'payment_failed'    // invoice payment failed — show "fix billing" CTA
  | 'invoice_processing'// invoice in-flight — auto-retries, just inform user
  | 'team_inactive';    // org subscription cancelled/inactive

export interface UsageLimitInfo {
  limitType:
    | 'monthly_cost'
    | 'monthlySpend'
    | 'teamUsage'
    | 'imageGenerations'
    | 'projects';
  currentUsage: {
    used: number;
    max: number;
    unit: string; // 'credits', 'dollars', etc.
  };
  planName: string; // 'free', 'plus', 'business', etc.
  message: string;
  title?: string;
  description?: string;
  variant?: UsageLimitVariant;
  /** When true, all plans above current show an upgrade button (for general upgrade CTAs).
   *  When false/unset, only the immediately next plan shows a button (for limit/feature gates). */
  showAllPlans?: boolean;
}

export interface PendingRequest {
  id: string;
  type: 'inline_ai' | 'chat' | 'stream';
  description: string;
  retryFunction: () => Promise<any>;
  context: any;
  resolve?: (result: any) => void;
  reject?: (error: any) => void;
}

interface UpgradeModalState {
  isOpen: boolean;
  limitInfo?: UsageLimitInfo;
  pendingRequest?: PendingRequest;
  upgradeInProgress: boolean;
  selectedTier?: 'basic' | 'standard' | 'premium' | 'payg';  // Selected plan tier for upgrade
  context?: UpgradeModalContext;
}

export interface UpgradeModalContext {
  /** Identifies which surface triggered this modal — used as the analytics source slug. */
  source: string;
  /** Describes which feature/limit gate is blocking the user. */
  feature_gate?: string;
  /** Upgrade plan tier surfaced to the user (if known). */
  plan_offered?: string;
  /**
   * Explicit target org for this modal's checkout/plan-privilege resolution.
   * When set, UpgradeModal/PaymentStep bill and evaluate privileges for THIS
   * org instead of falling back to the user's globally "active" dataspace
   * context — required for callers like OrgBillingTab that can show billing
   * for an org the user isn't currently "in".
   */
  orgId?: string;
  /** Chat thread that originated this upgrade (set when source === 'chat_paywall'). */
  threadId?: string;
  /** Space that originated this upgrade (set when source === 'chat_paywall'). */
  spaceId?: string;
  /**
   * For the onboarding_complete CTA: the exact prompt to auto-run after the
   * upgrade settles (instead of the default "Continue." injection). Survives the
   * Stripe Checkout redirect via the chat-paywall-resume payload.
   */
  resumePrompt?: string;
}

/**
 * sessionStorage key that survives the Stripe Checkout full-page redirect and
 * carries the chat thread context back to SpaceChat so it can inject a
 * "Continue." user message into the same thread after the upgrade settles.
 *
 * Set by showUpgradeModal when ctx.source === 'chat_paywall'.
 * Cleared by SpaceChat's plan-upgrade-resume effect, hideUpgradeModal, and completeUpgrade.
 */
export const CHAT_PAYWALL_RESUME_KEY = 'chat-paywall-resume';

const clearChatPaywallResume = () => {
  try {
    sessionStorage.removeItem(CHAT_PAYWALL_RESUME_KEY);
  } catch {
    // sessionStorage unavailable (SSR, private mode quota errors) — no-op.
  }
};

interface UsageLimitsStore {
  modal: UpgradeModalState;
  
  // Show upgrade modal with optional request continuation
  showUpgradeModal: (limitInfo: UsageLimitInfo, pendingRequest?: PendingRequest, ctx?: UpgradeModalContext) => void;
  
  // Hide upgrade modal
  hideUpgradeModal: () => void;
  
  // Complete successful upgrade (closes modal without rejecting)
  completeUpgrade: () => void;
  
  // Start upgrade process (sets upgradeInProgress flag)
  startUpgradeProcess: () => void;
  
  // Continue pending request after successful upgrade
  continuePendingRequest: () => Promise<any>;
  
  // Set selected tier
  setSelectedTier: (tier: 'basic' | 'standard' | 'premium' | 'payg') => void;
  
  // Handle upgrade completion
  handleUpgrade: () => void;
  
  // Handle upgrade cancellation
  handleCancel: () => void;
  
  // Check if upgrade is in progress
  isUpgrading: () => boolean;
}

const useUsageLimitsStore = create<UsageLimitsStore>((set, get) => ({
  modal: {
    isOpen: false,
    upgradeInProgress: false,
  },

  showUpgradeModal: (limitInfo: UsageLimitInfo, pendingRequest?: PendingRequest, ctx?: UpgradeModalContext) => {
    const { modal } = get();
    
    // Prevent showing multiple modals
    if (modal.isOpen) {
      log.info('[UsageLimitsStore] Modal already open, ignoring duplicate request');
      return;
    }
    
    log.info('[UsageLimitsStore] Showing upgrade modal:', {
      limitType: limitInfo.limitType,
      planName: limitInfo.planName,
      hasPendingRequest: !!pendingRequest
    });

    // Emit upgrade_cta_viewed via the analytics facade
    if (ctx?.source) {
      import('../services/analytics/analytics').then(({ analytics }) => {
        analytics.track('upgrade_cta_viewed', {
          source: ctx.source,
          feature_gate: ctx.feature_gate,
          plan_offered: ctx.plan_offered,
        });
      }).catch(() => {});
    }

    // Chat-paywall openings need to bridge their thread/space context across
    // the full-page Stripe Checkout redirect. Persist to sessionStorage so
    // SpaceChat can inject a "Continue." user turn when the user lands back.
    if (ctx?.source === 'chat_paywall' && ctx.threadId && ctx.spaceId) {
      try {
        sessionStorage.setItem(CHAT_PAYWALL_RESUME_KEY, JSON.stringify({
          threadId: ctx.threadId,
          spaceId: ctx.spaceId,
          // Onboarding CTA carries the exact next-step prompt to auto-run post-upgrade.
          prompt: ctx.resumePrompt,
          openedAt: Date.now(),
        }));
      } catch {
        // sessionStorage unavailable — inline path still works via modal.context.
      }
    }

    // Pre-select the tier the caller is surfacing (e.g. OrgBillingTab's "Select
    // Standard" card) so the modal opens directly on that plan instead of
    // always defaulting to 'basic' regardless of what was actually offered.
    const validTiers = ['basic', 'standard', 'premium', 'payg'] as const;
    const offeredTier = validTiers.find(t => t === ctx?.plan_offered);

    set({
      modal: {
        isOpen: true,
        limitInfo,
        pendingRequest,
        upgradeInProgress: false,
        selectedTier: offeredTier,
        context: ctx,
      }
    });
  },

  hideUpgradeModal: () => {
    const { modal } = get();
    
    // If there's a pending request and we're not upgrading, reject it
    if (modal.pendingRequest && !modal.upgradeInProgress && modal.pendingRequest.reject) {
      modal.pendingRequest.reject(new Error('User cancelled upgrade'));
    }

    // Emit checkout_abandoned if the modal was closed while a checkout was in progress
    if (modal.upgradeInProgress) {
      import('../services/analytics/analytics').then(({ analytics }) => {
        analytics.track('checkout_abandoned', {
          source: 'upgrade_modal',
          reason: 'modal_closed',
        });
      }).catch(() => {});
    }

    clearChatPaywallResume();

    set({
      modal: {
        isOpen: false,
        upgradeInProgress: false,
      }
    });
  },

  completeUpgrade: () => {
    log.info('[UsageLimitsStore] Upgrade completed successfully');

    clearChatPaywallResume();

    set({
      modal: {
        isOpen: false,
        upgradeInProgress: false,
      }
    });
  },

  startUpgradeProcess: () => {
    const { modal } = get();
    
    log.info('[UsageLimitsStore] Starting upgrade process (inline payment)');

    // Emit upgrade_cta_clicked — user has committed to the upgrade flow
    import('../services/analytics/analytics').then(({ analytics }) => {
      analytics.track('upgrade_cta_clicked', {
        source: 'upgrade_modal',
        feature_gate: modal.limitInfo?.limitType,
      });
    }).catch(() => {});
    
    set({
      modal: {
        ...modal,
        upgradeInProgress: true,
      }
    });
  },

  setSelectedTier: (tier: 'basic' | 'standard' | 'premium' | 'payg') => {
    const { modal } = get();
    
    log.info(`[UsageLimitsStore] Setting selected tier: ${tier}`);
    
    set({
      modal: {
        ...modal,
        selectedTier: tier,
      }
    });
  },

  // 🔥 Continue pending request after successful upgrade
  continuePendingRequest: async () => {
    const { modal } = get();
    if (modal.pendingRequest?.retryFunction) {
      try {
        console.log('[UsageLimitsStore] Continuing pending request:', modal.pendingRequest.description);
        const result = await modal.pendingRequest.retryFunction();
        
        // Resolve the original promise
        if (modal.pendingRequest.resolve) {
          modal.pendingRequest.resolve(result);
        }
        
        // Clear the modal
        set({
          modal: {
            isOpen: false,
            upgradeInProgress: false,
          }
        });
        
        return result;
      } catch (error) {
        console.error('[UsageLimitsStore] Error continuing request:', error);
        
        // Reject the original promise
        if (modal.pendingRequest.reject) {
          modal.pendingRequest.reject(error);
        }
        
        // Clear the modal
        set({
          modal: {
            isOpen: false,
            upgradeInProgress: false,
          }
        });
        
        throw error;
      }
    }
  },

  handleUpgrade: async () => {
    const { modal } = get();
    
    log.info('[UsageLimitsStore] handleUpgrade called - DEPRECATED');
    log.warn('[UsageLimitsStore] This method is deprecated. UpgradeModal now handles payment inline using multi-step flow.');
    
    // DEPRECATED: This method previously redirected to /signup
    // Now the UpgradeModal uses a multi-step flow:
    // Step 1: Select plan (existing tier cards)
    // Step 2: Enter payment (inline Stripe form)
    // 
    // Buttons in UpgradeModal and OrgBillingTab now call:
    // - setStoreSelectedTier() to set the selected plan
    // - setStep('enter-payment') to show the payment form
    // 
    // Keeping this method for backward compatibility but it does nothing.
    
    set({
      modal: {
        ...modal,
        upgradeInProgress: false,
      }
    });
  },

  handleCancel: () => {
    const { modal } = get();
    
    log.info('[UsageLimitsStore] Upgrade cancelled by user');
    
    if (modal.pendingRequest && modal.pendingRequest.reject) {
      modal.pendingRequest.reject(new Error('User cancelled upgrade'));
    }
    
    get().hideUpgradeModal();
  },

  isUpgrading: () => {
    return get().modal.upgradeInProgress;
  },
}));

export default useUsageLimitsStore;

// Convenience function for intercepting LLM requests
export const interceptLLMRequest = async function<T>(
  requestType: 'inline_ai' | 'chat' | 'stream',
  requestFunction: () => Promise<T>,
  context: {
    userPrompt?: string;
    selectedText?: string;
    operation?: string;
  }
): Promise<T> {
  const store = useUsageLimitsStore.getState();
  
  try {
    return await requestFunction();
  } catch (error: any) {
    // 402 Payment Required is a billing limit; 403 with certain upgrade signals are also billing.
    const isLimitError = error.status === 402 || (error.status === 403 && (
      error.data?.upgrade_required === true ||
      error.data?.detail?.upgrade_required === true ||
      error.data?.error?.includes?.('LIMIT_EXCEEDED') ||
      error.data?.detail?.error?.includes?.('LIMIT_EXCEEDED') ||
      error.data?.error?.includes?.('COST_LIMIT_EXCEEDED') ||
      error.data?.detail?.error?.includes?.('COST_LIMIT_EXCEEDED') ||
      error.data?.error === 'MONTHLY_COST_LIMIT_EXCEEDED' ||
      error.data?.detail?.error === 'MONTHLY_COST_LIMIT_EXCEEDED' ||
      error.data?.error === 'MEMBER_BUDGET_EXCEEDED' ||
      error.data?.detail?.error === 'MEMBER_BUDGET_EXCEEDED' ||
      error.message?.includes?.('limit') ||
      error.message?.includes?.('upgrade')
    ));

    if (error.code === 'TOKEN_LIMIT_EXCEEDED') {
      throw error;
    }

    // spend_limit_consent_required is handled inline by chatStore — the
    // SpendLimitApprovalCard is already injected into the thread. Let the
    // error bubble so callers can clean up state without us also opening the
    // upgrade modal on top.
    const _earlyDetail = error.data?.detail || error.data;
    if (_earlyDetail?.reason === 'spend_limit_consent_required') {
      throw error;
    }

    if (isLimitError) {
      useThinkingLoaderStore.getState().stopWaiting();

      try {
        const { default: usePromoStore } = await import('../stores/promoStore');
        await usePromoStore.getState().fetchStatus();
      } catch {
        // Promo check is non-critical — continue regardless.
      }
      
      // Create retry function
      const retryFunction = async () => {
        log.info('[InterceptLLM] Retrying request after upgrade');
        return await requestFunction();
      };
      
      // Create pending request
      const pendingRequest: PendingRequest = {
        id: `${requestType}_${Date.now()}`,
        type: requestType,
        description: context.userPrompt || context.operation || 'AI request',
        retryFunction,
        context
      };
      
      // Create limit info from error - handle FastAPI detail wrapping
      const errorDetail = error.data?.detail || error.data;
      const errorCode = errorDetail?.error;
      const errorReason = errorDetail?.reason;

      // Special case: payment failed while user was in overage or on PAYG.
      // AI usage is immediately paused — show a clear "fix payment" message,
      // not an upgrade prompt.
      if (errorCode === 'MEMBER_BUDGET_EXCEEDED' && errorReason === 'payment_failed_overage_blocked') {
        const limitInfo: UsageLimitInfo = {
          limitType: 'monthly_cost',
          currentUsage: { used: 0, max: 0, unit: 'dollars' },
          planName: errorDetail?.billing_plan || 'unknown',
          message: 'Payment update required.',
          title: 'Payment update required',
          description: "We couldn't collect your most recent AI usage invoice. Update your payment method in billing settings to resume.",
          variant: 'payment_failed',
          showAllPlans: false,
        };
        await new Promise(resolve => setTimeout(resolve, 500));
        return new Promise((resolve, reject) => {
          pendingRequest.resolve = resolve;
          pendingRequest.reject = reject;
          store.showUpgradeModal(limitInfo, pendingRequest);
        });
      }

      // Special case: progressive billing cap hit — an invoice is in flight and
      // payment should clear within minutes. No upgrade needed; just inform the user.
      if (errorCode === 'MEMBER_BUDGET_EXCEEDED' && errorReason === 'payg_unbilled_cap_reached') {
        const limitInfo: UsageLimitInfo = {
          limitType: 'monthly_cost',
          currentUsage: { used: 0, max: 0, unit: 'dollars' },
          planName: errorDetail?.billing_plan || 'payg',
          message: 'Invoice processing.',
          title: 'Invoice processing, AI paused',
          description: "We're collecting your most recent AI usage invoice now. AI will resume automatically within minutes once payment is confirmed.",
          variant: 'invoice_processing',
          showAllPlans: false,
        };
        await new Promise(resolve => setTimeout(resolve, 500));
        return new Promise((resolve, reject) => {
          pendingRequest.resolve = resolve;
          pendingRequest.reject = reject;
          store.showUpgradeModal(limitInfo, pendingRequest);
        });
      }

      // Special case: team subscription is inactive (owner cancelled).
      // Show an informational notice rather than an upgrade prompt.
      if (errorCode === 'MEMBER_BUDGET_EXCEEDED' && errorReason === 'team_subscription_inactive') {
        const limitInfo: UsageLimitInfo = {
          limitType: 'monthly_cost',
          currentUsage: { used: 0, max: 0, unit: 'dollars' },
          planName: errorDetail?.billing_plan || 'free',
          message: "Your team's subscription is inactive.",
          title: 'Team subscription inactive',
          description: "Your team's subscription is currently inactive. AI usage has been paused. Ask the team owner to reactivate the subscription.",
          variant: 'team_inactive',
          showAllPlans: false,
        };
        await new Promise(resolve => setTimeout(resolve, 500));
        return new Promise((resolve, reject) => {
          pendingRequest.resolve = resolve;
          pendingRequest.reject = reject;
          store.showUpgradeModal(limitInfo, pendingRequest);
        });
      }

      // Special case: onboarding complete — the guided flow finished and the free
      // budget was drained. Show a full-plan upgrade modal (same as the CTA button
      // in the chat). Unlike the CTA button path there is no resumePrompt here
      // because the user sent a new arbitrary message rather than clicking the CTA.
      if (errorCode === 'MEMBER_BUDGET_EXCEEDED' && errorReason === 'onboarding_complete') {
        const limitInfo: UsageLimitInfo = {
          limitType: 'monthly_cost',
          currentUsage: { used: 0, max: 0, unit: 'dollars' },
          planName: errorDetail?.billing_plan || 'free',
          message: 'Upgrade to keep going.',
          title: 'Upgrade to keep going',
          description: 'Pick a plan to continue right where you left off.',
          variant: 'upgrade',
          showAllPlans: true,
        };
        await new Promise(resolve => setTimeout(resolve, 500));
        return new Promise((resolve, reject) => {
          pendingRequest.resolve = resolve;
          pendingRequest.reject = reject;
          store.showUpgradeModal(limitInfo, pendingRequest);
        });
      }

      // Generic per-user budget exceeded. Fields are monthly_spend_usd / monthly_budget_usd
      // (dollars) for MEMBER_BUDGET_EXCEEDED, or limits.monthly_cost.used/max for legacy errors.
      let usedCents: number;
      let maxCents: number;
      if (errorCode === 'MEMBER_BUDGET_EXCEEDED') {
        usedCents = Math.round((errorDetail?.monthly_spend_usd ?? 0) * 100);
        maxCents  = Math.round((errorDetail?.monthly_budget_usd ?? 0) * 100);
      } else {
        const usedValue = errorDetail?.limits?.monthly_cost?.used || errorDetail?.limits?.used || 0;
        const maxValue  = errorDetail?.limits?.monthly_cost?.max  || errorDetail?.limits?.max  || 1000;
        const isMonthlyCredit = errorDetail?.limit_type === 'monthly_cost';
        usedCents = isMonthlyCredit ? Math.round(usedValue * 100) : usedValue;
        maxCents  = isMonthlyCredit ? Math.round(maxValue  * 100) : maxValue;
      }

      const limitInfo: UsageLimitInfo = {
        limitType: errorDetail?.limit_type || 'monthly_cost',
        currentUsage: { used: usedCents, max: maxCents, unit: 'dollars' },
        planName: errorDetail?.plan_type || errorDetail?.billing_plan || 'free',
        message: errorDetail?.message || 'You have reached your usage limit.',
      };

      await new Promise(resolve => setTimeout(resolve, 500));
      return new Promise((resolve, reject) => {
        pendingRequest.resolve = resolve;
        pendingRequest.reject = reject;
        store.showUpgradeModal(limitInfo, pendingRequest);
      });
    } else {
      throw error;
    }
  }
}
